import React, { useState, useEffect, Suspense, useMemo } from 'react';
import { type QuickCreateAdapter, QuickCreateProvider } from '@/hooks/use-quick-create';
import { QUICK_CREATE_CONFIGS } from '@/configs/quick-create-config';
import { QUERY_KEYS } from '@/constants/query-keys';
import { useCRUD } from '@/hooks/queries/useCRUD';

// Dynamic glob import of all feature Form Dialogs matching standard patterns
const formModules = import.meta.glob([
  '../../features/**/components/FormDialog.tsx',
  '../../features/**/components/form-dialog.tsx',
  '../../features/**/components/FormDialog/index.tsx',
  '../../features/**/components/form-dialog/index.tsx',
  '../../features/**/components/service-dialog/index.tsx'
]);

interface DynamicQuickCreateWrapperProps {
  configKey: string;
  initialData: any;
  mode: 'add' | 'edit';
  onClose: () => void;
  onSaved: (item: any) => void;
  open: boolean;
}

function DynamicQuickCreateWrapper({
  configKey,
  initialData,
  mode,
  onClose,
  onSaved,
  open
}: DynamicQuickCreateWrapperProps) {
  const [LoadedFormComponent, setLoadedFormComponent] = useState<React.ComponentType<any> | null>(null);
  const config = QUICK_CREATE_CONFIGS[configKey];

  // Resolve endpoint dynamically: manual config endpoint or the key itself
  const endpoint = config?.endpoint || configKey;

  const { addItem, updateItem } = useCRUD({
    endpoint,
    initialFetch: false
  });

  useEffect(() => {
    if (!open) return;

    let importPromise: Promise<any> | null = null;

    if (config?.importForm) {
      importPromise = config.importForm();
    } else {
      // Find path matching the feature key convention
      let cleanPathKey = configKey.split('?')[0];
      if (configKey.includes('is_vendor=true')) {
        cleanPathKey = 'danh-muc/doi-tuong/nha-cung-cap';
      }
      const searchPattern = `features/${cleanPathKey}/components/`;
      const matchedPath = Object.keys(formModules).find(path => path.replace(/\\/g, '/').includes(searchPattern));

      if (matchedPath) {
        const loader = formModules[matchedPath];
        importPromise = loader();
      }
    }

    if (importPromise) {
      importPromise
        .then(module => {
          const Component =
            config && config.componentName
              ? module[config.componentName]
              : module.default || module.FormDialog || module.ServiceDialog || module.Dialog;
          setLoadedFormComponent(() => Component);
        })
        .catch(err => {
          console.error(`Failed to dynamic import quick create form for key: ${configKey}`, err);
        });
    } else {
      console.warn(`No Form Dialog found for quick create key: ${configKey}`);
    }
  }, [open, configKey, config]);

  if (!open || !LoadedFormComponent) {
    return null;
  }

  const handleSubmit = async (data: any) => {
    try {
      const savedItem =
        mode === 'edit' && initialData?.uuid ? await updateItem(initialData.uuid, data) : await addItem(data);
      onSaved(savedItem);
    } catch (err) {
      console.error('Failed to submit quick create form:', err);
    }
  };

  return (
    <Suspense fallback={null}>
      <LoadedFormComponent
        mode={mode}
        formMode={mode}
        open={open}
        onClose={onClose}
        initialData={mode === 'edit' ? initialData : undefined}
        onSubmit={handleSubmit}
        onSaved={onSaved}
      />
    </Suspense>
  );
}

export function GlobalQuickCreateProvider({ children }: { children: React.ReactNode }) {
  const adapters = useMemo<Record<string, QuickCreateAdapter>>(() => {
    const registry: Record<string, QuickCreateAdapter> = {};

    // 1. Register from manual config
    Object.keys(QUICK_CREATE_CONFIGS).forEach(key => {
      registry[key] = {
        render: ({ initialData, mode, onClose, onSaved, open }) => (
          <DynamicQuickCreateWrapper
            configKey={key}
            initialData={initialData}
            mode={mode}
            onClose={onClose}
            onSaved={onSaved}
            open={open}
          />
        )
      };
    });

    // 2. Automatically register all QUERY_KEYS that contain slashes and start with 'danh-muc/'
    Object.values(QUERY_KEYS).forEach(endpoint => {
      if (typeof endpoint === 'string' && endpoint.includes('/') && endpoint.startsWith('danh-muc/')) {
        const key = endpoint.replace(/^\/+|\/+$/g, '');
        if (!registry[key]) {
          registry[key] = {
            render: ({ initialData, mode, onClose, onSaved, open }) => (
              <DynamicQuickCreateWrapper
                configKey={key}
                initialData={initialData}
                mode={mode}
                onClose={onClose}
                onSaved={onSaved}
                open={open}
              />
            )
          };
        }
      }
    });

    return registry;
  }, []);

  return <QuickCreateProvider adapters={adapters}>{children}</QuickCreateProvider>;
}
